import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  IconButton,
  TextField,
  CircularProgress,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  LocationOn as LocationIcon,
  EmojiEmotions as EmojiIcon,
  GridOn as GridIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Import extracted components and constants
import FilePreviewItem from "./messageInput/FilePreviewItem";
import EmojiPicker from "./messageInput/EmojiPicker";
import MentionList from "./messageInput/MentionList";
import ReplyPreview from "./messageInput/ReplyPreview";

// Styled MessageInputContainer
const MessageInputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
  position: "relative",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5),
    gap: theme.spacing(0.5),
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    zIndex: 9999,
    boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
    borderTop: `2px solid ${theme.palette.divider}`,
    minHeight: "64px",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: "env(safe-area-inset-bottom, 16px)",
    transform: "translateZ(0)",
    willChange: "transform",
    transition: "transform 0.2s ease-out",
    height: "auto",
    maxHeight: "none",
    touchAction: "manipulation",
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1),
    gap: theme.spacing(0.25),
    minHeight: "60px",
    left: 0,
    right: 0,
    width: "100vw",
    zIndex: 10000,
  },
}));

/**
 * Optimized Message Input Component
 */
const MessageInput = React.memo(({
  newMessage = "",
  onInputChange,
  onSendMessage,
  onFileSelect,
  onOpenChatCategories,
  onKeyPress,
  isConnected,
  isUploading,
  selectedFiles = [],
  onRemoveFile,
  onClearAllFiles,
  uploadProgress,
  conversationInfo,
  isSidebarOpen = false,
  replyPreview,
  onCancelReply,
  onMentionsChange,
  onSendMessageWithFormattedText,
  isAiStreaming = false,
  fileInputId = "file-input",
  onOpenLocationDialog = null,
  quickReplySuggestions = [],
  onQuickReplySelect,
}) => {
  const theme = useTheme();
  const textInputRef = useRef(null);

  // States
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionStartPos, setMentionStartPos] = useState(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentions, setMentions] = useState([]);

  // Extract all members once
  const members = useMemo(() => {
    if (!conversationInfo) return [];
    const { admin = [], users = [] } = conversationInfo;
    const allMembers = [...admin, ...users];
    
    // Remove duplicates based on user ID
    const uniqueMembers = allMembers.filter((member, index, self) => {
      const userId = member?.userId?._id;
      return userId && self.findIndex(m => m?.userId?._id === userId) === index;
    });
    
    return uniqueMembers.map(member => ({
      id: member?.userId?._id,
      name: member?.userId?.fullName || member?.userId?.userName || "Unknown User",
      userName: member?.userId?.userName || "",
      avatar: member?.userId?.image?.profilePic,
      userType: member?.userType,
    })).filter(member => member.id);
  }, [conversationInfo]);

  // filtered members for mentions
  const filteredMembers = useMemo(() => {
    if (!mentionSearch) return members;
    const searchLower = mentionSearch.toLowerCase();
    return members.filter(member => 
      member.name.toLowerCase().includes(searchLower) ||
      member.userName.toLowerCase().includes(searchLower)
    );
  }, [members, mentionSearch]);

  // Reset mentions when message is cleared
  useEffect(() => {
    if (!newMessage || newMessage.trim() === "") {
      if (mentions.length > 0) {
        setMentions([]);
        if (onMentionsChange) onMentionsChange([]);
      }
    }
  }, [newMessage, onMentionsChange]);

  // format mentions in message text for socket transmission
  const formatMentionsForSocket = useCallback((text, mentionsArray) => {
    if (!mentionsArray || mentionsArray.length === 0) return text;
    
    let formattedText = text;
    // Sort mentions by startIndex in descending order to avoid index shifting issues
    const sortedMentions = [...mentionsArray].sort((a, b) => b.startIndex - a.startIndex);
    
    sortedMentions.forEach(mention => {
      const originalMention = `@${mention.name}`;
      const formattedMention = `<:${mention.name}>`;
      // Replace the original mention with formatted mention
      formattedText = formattedText.replace(originalMention, formattedMention);
    });
    
    return formattedText;
  }, []);

  const handleSend = useCallback(() => {
    if (!newMessage.trim() && selectedFiles.length === 0) return;
    if (!isConnected || isUploading || isAiStreaming) return;

    if (mentions.length > 0 && onSendMessageWithFormattedText) {
      const formattedMessage = formatMentionsForSocket(newMessage, mentions);
      onSendMessageWithFormattedText(formattedMessage, mentions);
      setMentions([]);
    } else {
      onSendMessage();
    }
  }, [newMessage, mentions.length, onSendMessage, onSendMessageWithFormattedText, formatMentionsForSocket, isConnected, isUploading, isAiStreaming, selectedFiles.length]);

  const handleEmojiSelect = useCallback((emoji) => {
    const textArea = textInputRef.current?.querySelector('textarea, input');
    if (textArea) {
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;
      const newText = newMessage.substring(0, start) + emoji + newMessage.substring(end);
      
      onInputChange({ target: { value: newText } });

      setTimeout(() => {
        const newPos = start + emoji.length;
        textArea.setSelectionRange(newPos, newPos);
        textArea.focus();
      }, 0);
    }
  }, [newMessage, onInputChange]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onInputChange(e);
    
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionStartPos(atIndex);
        setMentionSearch(textAfterAt);
        setShowMentionList(true);
        setSelectedMentionIndex(0);
        return;
      }
    }
    setShowMentionList(false);
  }, [onInputChange]);

  const handleMentionSelect = useCallback((member) => {
    const textArea = textInputRef.current?.querySelector('textarea, input');
    
    if (textArea && mentionStartPos !== null) {
      const beforeMention = newMessage.substring(0, mentionStartPos);
      const afterMention = newMessage.substring(textArea.selectionStart);
      const mentionText = `@${member.name} `;
      const newText = beforeMention + mentionText + afterMention;
      
      onInputChange({ target: { value: newText } });
      
      const newMention = {
        userId: member.id,
        name: member.name,
        startIndex: mentionStartPos,
        endIndex: mentionStartPos + mentionText.length,
      };
      const updatedMentions = [...mentions, newMention];
      setMentions(updatedMentions);
      if (onMentionsChange) onMentionsChange(updatedMentions);
      
      setShowMentionList(false);
      setMentionSearch("");
      setMentionStartPos(null);
      
      setTimeout(() => {
        const newCursorPos = beforeMention.length + mentionText.length;
        textArea.setSelectionRange(newCursorPos, newCursorPos);
        textArea.focus();
      }, 0);
    }
  }, [newMessage, mentionStartPos, mentions, onInputChange, onMentionsChange]);

  const handleMentionKeyDown = useCallback((e) => {
    if (!showMentionList) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev => 
        prev < filteredMembers.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter' && filteredMembers.length > 0) {
      e.preventDefault();
      handleMentionSelect(filteredMembers[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      setShowMentionList(false);
    }
  }, [showMentionList, filteredMembers, selectedMentionIndex, handleMentionSelect]);

  const handleLocalKeyPress = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (!showMentionList) {
        e.preventDefault();
        handleSend();
      }
      // If mention list is open, handleMentionKeyDown will handle it via onKeyDown
    } else {
      onKeyPress?.(e);
    }
  }, [showMentionList, handleSend, onKeyPress]);

  const getFileType = (file) => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    if (file.type.startsWith("audio/")) return "audio";
    if (file.type.includes("pdf") || file.type.includes("document") || file.type.includes("text"))
      return "document";
    return "file";
  };

  return (
    <>
      <ReplyPreview replyPreview={replyPreview} onCancelReply={onCancelReply} />

      {quickReplySuggestions.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            px: 2,
            py: 1,
            backgroundColor: (t) => t.palette.background.paper,
            [theme.breakpoints.down("md")]: {
              px: 1.5,
              py: 0.75,
              marginBottom: 0,
            },
          }}
        >
          {quickReplySuggestions.map((suggestion, index) => (
            <Button
              key={`${suggestion}-${index}`}
              size="small"
              variant="text"
              onClick={() => onQuickReplySelect?.(suggestion)}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                border: "none",
                color: "primary.main",
                backgroundColor: "rgba(124, 58, 237, 0.08)",
                fontSize: "0.8125rem",
                px: 1.5,
                py: 0.5,
                "&:hover": {
                  backgroundColor: "rgba(124, 58, 237, 0.14)",
                },
              }}
            >
              {suggestion}
            </Button>
          ))}
        </Box>
      )}

      {selectedFiles.length > 0 && (
        <Box sx={{
          padding: { xs: 1.5, sm: 2 },
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          backgroundColor: "rgba(0,0,0,0.02)",
          maxHeight: "200px",
          overflowY: "auto",
          position: "relative",
          zIndex: 999,
          [theme.breakpoints.down("sm")]: { marginBottom: "80px" },
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
              selected files ({selectedFiles.length})
            </Typography>
            <Button size="small" onClick={onClearAllFiles} sx={{ color: "#EF4444" }}>clear all</Button>
          </Box>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(auto-fill, minmax(100px, 1fr))", sm: "repeat(auto-fill, minmax(120px, 1fr))" },
            gap: { xs: 0.5, sm: 1 },
            mb: 2,
          }}>
            {selectedFiles.map((file, index) => (
              <FilePreviewItem key={index} file={file} index={index} onRemoveFile={onRemoveFile} getFileType={getFileType} />
            ))}
          </Box>
          {isUploading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, backgroundColor: "rgba(124, 58, 237, 0.1)", borderRadius: 1 }}>
              <CircularProgress size={16} sx={{ color: "#7C3AED" }} />
              <Typography variant="caption" sx={{ color: "#7C3AED", fontWeight: 500 }}>
                uploading... {uploadProgress?.overall || 0}%
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <MessageInputContainer>
        <IconButton size="small" onClick={() => setShowEmojiPicker(!showEmojiPicker)} sx={{ backgroundColor: showEmojiPicker ? "rgba(124, 58, 237, 0.1)" : "transparent", color: showEmojiPicker ? "#7C3AED" : "inherit" }}>
          <EmojiIcon />
        </IconButton>

        {showEmojiPicker && <EmojiPicker theme={theme} onEmojiSelect={handleEmojiSelect} onClose={() => setShowEmojiPicker(false)} />}

        <input type="file" multiple accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt" onChange={onFileSelect} style={{ display: "none" }} id={fileInputId} />
        <label htmlFor={fileInputId}><IconButton size="small" component="span"><AttachFileIcon /></IconButton></label>

        <IconButton size="small" onClick={onOpenLocationDialog || undefined}><LocationIcon /></IconButton>
        {conversationInfo?.groupType === "business" && (<IconButton size="small" onClick={onOpenChatCategories}><GridIcon /></IconButton>)}

        <Box ref={textInputRef} sx={{ flex: 1, position: 'relative' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="type a message... (use @ to mention)"
            value={newMessage}
            onChange={handleInputChange}
            onKeyPress={handleLocalKeyPress}
            onKeyDown={handleMentionKeyDown}
            multiline
            maxRows={4}
            name="message"
            sx={{
              "& .MuiInputBase-root": { fontSize: { xs: "0.875rem", sm: "1rem" }, padding: { xs: 0.5, sm: 1 } },
              [theme.breakpoints.down("md")]: {
                "& .MuiInputBase-root": {
                  fontSize: "0.875rem",
                  padding: "8px 12px",
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  minHeight: "40px",
                },
              },
            }}
          />

          {showMentionList && <MentionList members={filteredMembers} selectedIndex={selectedMentionIndex} onSelect={handleMentionSelect} theme={theme} />}
        </Box>

        <IconButton
          onClick={handleSend}
          disabled={(!newMessage.trim() && selectedFiles.length === 0) || !isConnected || isUploading || isAiStreaming}
          sx={{
            backgroundColor: isAiStreaming ? "#10B981" : "#7C3AED",
            color: "white",
            padding: { xs: 0.75, sm: 1 },
            "&:hover": { backgroundColor: isAiStreaming ? "#059669" : "#6D28D9" },
            "&:disabled": { backgroundColor: "#D1D5DB", color: "#9CA3AF" },
          }}
        >
          {isUploading || isAiStreaming ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        </IconButton>
      </MessageInputContainer>
    </>
  );
});

export default MessageInput;
